import React from 'react';
import { ExclamationOutlined, LoadingOutlined } from '@ant-design/icons';
import { ReactComponent as JollyFace } from 'assets/images/happy-face.svg';

export type PaymentStatusType = 'SUCCESS' | 'FAILED' | 'UNKNOWN' | 'PROCESSING';

interface PaymentStatusMessageProps {
  status: PaymentStatusType;
  thankYouMessage?: string;
  responseMessage?: string;
  orderId?: string | null;
  countdown?: number;
  onRetry?: () => void;
}

const PaymentStatusMessage: React.FC<PaymentStatusMessageProps> = ({
  status,
  thankYouMessage = 'Thanks for choosing us, come back soon for more fun!',
  responseMessage,
  orderId,
  countdown = 5,
  onRetry,
}) => {
  if (status === 'SUCCESS') {
    return (
      <div className='flex flex-col items-center justify-center h-full paymentStatusPage text-center mx-10'>
        <div className='happyFaceIcon'>
          <JollyFace className='mb-[20px] iconColor' />
        </div>
        <div className='successMessage'>Payment Successful.</div>
        <div className='my-[10px]'>{thankYouMessage}</div>
      </div>
    );
  }

  if (status === 'FAILED') {
    return (
      <div className='flex flex-col justify-center items-center h-full paymentConfirmationContentWrapper paymentFailed'>
        <div className='paymentFailedIconWrapper p-5 my-5'>
          <ExclamationOutlined
            className='paymentFailedIcon'
            style={{ fontSize: '48px', color: '#ff4d4f' }}
          />
        </div>
        <div className='text-center text-lg font-semibold'>Payment Failed</div>
        {responseMessage && <div className='text-center text-gray-600 mt-2'>{responseMessage}</div>}
        <div className='paymentFailedCountdown text-center mt-4 text-gray-500'>
          <span>You will be redirected to checkout in </span>
          <span className='font-semibold'>
            {countdown} {countdown <= 1 ? 'second' : 'seconds'}
          </span>
          .
        </div>
        {onRetry && (
          <button
            onClick={onRetry}
            className='mt-6 px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md font-medium transition-colors'
          >
            Retry Payment
          </button>
        )}
      </div>
    );
  }

  if (status === 'UNKNOWN') {
    return (
      <div className='flex flex-col justify-center items-center h-full paymentConfirmationContentWrapper paymentFailed'>
        <div className='paymentFailedIconWrapper p-5 my-5'>
          <ExclamationOutlined
            className='paymentFailedIcon'
            style={{ fontSize: '48px', color: '#faad14' }}
          />
        </div>
        <div className='text-center text-lg font-semibold'>Unable to confirm payment status</div>
        <div className='text-center text-gray-600 mt-2'>
          We could not verify the payment for Order ID {orderId}. Please check with the staff.
        </div>
        {onRetry && (
          <button
            onClick={onRetry}
            className='mt-6 px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md font-medium transition-colors'
          >
            Retry Payment
          </button>
        )}
      </div>
    );
  }

  // default / PROCESSING
  return (
    <div className='flex flex-col justify-center items-center h-full paymentConfirmationContentWrapper paymentProcessing'>
      <LoadingOutlined className='my-5 paymentProcessingIcon' style={{ fontSize: '48px' }} />
      <div className='text-center text-lg font-semibold mt-4'>
        {responseMessage || 'Processing your payment...'}
      </div>
      <div className='text-center text-gray-500 mt-2'>Please don&apos;t close this window.</div>
    </div>
  );
};

export default PaymentStatusMessage;
